import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/accountRole";
import Navbar from "@/components/Navbar";
import BuyerPassModal, { openBuyerPass } from "@/components/BuyerPassModal";
import { Crosshair } from "@/components/Crosshair";
import { SellerVerificationChip } from "@/components/SellerVerificationChip";
import { VouchMark } from "@/components/VouchMark";
import { FoundingPlate } from "@/components/FoundingPlate";
import { timeAgo } from "@/lib/constants";
import {
  MapPin, Truck, Repeat, Flag, MessageSquare, Send, ArrowUpToLine, Pencil,
  CheckCircle2, AlertTriangle, ShieldAlert, ShieldCheck, ShieldOff, Eye, Star, ShoppingCart, Lock, Info, Megaphone,
  ChevronLeft, ChevronRight, Share2, Tag, ArrowUpRight, Link2,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "@/lib/toast";
import { magLabel, WTB_TIMEFRAMES, listingPath } from "@/lib/constants";
import { BlockUserButton } from "@/components/BlockUserButton";
import { BoostListingButton } from "@/components/BoostListingButton";
import { CloseListingButton } from "@/components/CloseListingButton";
import { MakeOfferModal } from "@/components/MakeOfferModal";
import { CheckoutModal } from "@/components/CheckoutModal";

const brandSlug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function Spec({ label, value, to }) {
  if (!value) return null;
  return (
    <div className="border border-sniper bg-surface px-3 py-2">
      <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      {to ? (
        <Link to={to} data-testid="spec-brand-link"
          className="font-heading text-lg text-olive-bright hover:text-olive-bright/80 leading-tight inline-flex items-center gap-1 transition-colors">
          {value} <ArrowUpRight className="w-3.5 h-3.5 shrink-0" />
        </Link>
      ) : (
        <div className="font-heading text-lg text-foreground leading-tight">{value}</div>
      )}
    </div>
  );
}

// Optional provenance: prefer an original purchase date (as "Purchased Mon YYYY"), else the
// seller's free-text approximation ("2 years"). Returns null when neither is set.
function ageLabel(listing) {
  const pd = (listing.purchase_date || "").trim();
  if (pd) {
    const d = new Date(pd);
    if (!isNaN(d)) return `Purchased ${d.toLocaleDateString(undefined, { month: "short", year: "numeric" })}`;
    return `Purchased ${pd}`;
  }
  return (listing.approx_age || "").trim() || null;
}

// Neutral scope silhouette — shown whenever a photo is missing or fails to load,
// so the native broken-image icon never appears.
function ScopeSilhouette({ className = "" }) {
  return (
    <svg viewBox="0 0 72 44" className={className} fill="currentColor" aria-hidden="true">
      <rect x="8" y="17" width="46" height="11" rx="3" />
      <rect x="2" y="14" width="7" height="17" rx="2" />
      <rect x="52" y="12" width="9" height="21" rx="2" />
      <rect x="28" y="7" width="7" height="12" rx="2" />
    </svg>
  );
}

function PhotoPlaceholder({ small = false }) {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-surface text-muted-foreground/50 select-none" data-testid="photo-placeholder">
      <ScopeSilhouette className={small ? "w-8 h-5" : "w-16 h-10"} />
      {!small && <span className="font-mono text-[10px] uppercase tracking-wide mt-2 text-muted-foreground">No photo provided</span>}
    </div>
  );
}

// Real brand marks (currentColor) so the share row shows genuine logos, not generic outlines.
const FacebookMark = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true"><path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.628-5.373-12-12-12s-12 5.372-12 12c0 5.628 3.874 10.35 9.101 11.647Z"/></svg>
);
const InstagramMark = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162s2.76 6.162 6.162 6.162 6.162-2.76 6.162-6.162-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/></svg>
);
const XMark = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
);

function ShareActionButton({ testid, label, onClick, children }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} title={label}
      className="flex flex-col items-center justify-center gap-1 py-2 border border-sniper text-secondary-foreground hover:text-olive-bright hover:border-olive/60 transition-colors">
      {children}
      <span className="font-mono text-[8.5px] uppercase tracking-wide leading-none">{label}</span>
    </button>
  );
}

// The single share surface: a collapsible panel with the exact 1200x630 preview card AND a row of
// share actions right below it (so the actions live next to the thing they share). Visible to any
// viewer. Every action shares the human-readable /api/l/<username>/<seq> link, which unfurls with
// this listing's own card (photo/title/price) and 302-redirects a human click to the listing.
function SharePreview({ listingId, shareUrl, title, open, setOpen, panelRef }) {
  const smsBody = `${title} ${shareUrl}`;
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied — it shows a preview with the photo, title and price."); } catch { /* noop */ }
  };
  const shareToInstagram = async () => {
    try { await navigator.clipboard.writeText(smsBody); } catch { /* noop */ }
    toast.success("Title + link copied — paste it into your Instagram story, post or DM.");
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
  };
  return (
    <div ref={panelRef} className="mt-4 border border-sniper" data-testid="share-preview" style={{ scrollMarginTop: 96 }}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="share-preview-toggle"
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 font-mono uppercase text-[11px] text-secondary-foreground hover:text-foreground transition-colors">
        <span className="flex items-center gap-2"><Share2 className="w-4 h-4" /> Share this listing</span>
        <ChevronRight className={`w-4 h-4 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3" data-testid="share-preview-body">
          <div className="border border-sniper bg-obsidian overflow-hidden" style={{ aspectRatio: "1200 / 630" }}>
            <img src={`${API}/listings/${listingId}/og-image.jpg?t=${Date.now()}`} alt="Share card preview"
              className="w-full h-full object-cover" data-testid="share-preview-image" />
          </div>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
            This is what people see when your link is pasted.
          </p>
          <div className="mt-2 grid grid-cols-5 gap-1.5" data-testid="share-actions">
            <ShareActionButton testid="share-copy" label="Copy link" onClick={copyLink}><Link2 className="w-4 h-4" /></ShareActionButton>
            <ShareActionButton testid="share-sms" label="SMS" onClick={() => { window.location.href = `sms:?&body=${encodeURIComponent(smsBody)}`; }}><MessageSquare className="w-4 h-4" /></ShareActionButton>
            <ShareActionButton testid="share-facebook" label="Facebook" onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank", "noopener,noreferrer")}><FacebookMark /></ShareActionButton>
            <ShareActionButton testid="share-x" label="X" onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`, "_blank", "noopener,noreferrer")}><XMark /></ShareActionButton>
            <ShareActionButton testid="share-instagram" label="Instagram" onClick={shareToInstagram}><InstagramMark /></ShareActionButton>
          </div>
        </div>
      )}
    </div>
  );
}

// <img> with an onError fallback to the neutral placeholder (never the browser
// broken-image glyph). Resets its error flag whenever the src changes.
function ListingImage({ src, alt = "", className = "", small = false }) {
  const [err, setErr] = useState(false);
  useEffect(() => { setErr(false); }, [src]);
  if (!src || err) return <PhotoPlaceholder small={small} />;
  return <img src={src} alt={alt} className={className} onError={() => setErr(true)} loading="lazy" />;
}

// Never let an absurd price ($100 quintillion etc.) force horizontal page scroll on mobile —
// compact-notate anything huge; the element is also min-w-0 + truncate as a hard backstop.
const fmtDetailPrice = (n) => {
  if (!(n > 0)) return "Contact";
  if (n >= 1e7) return `$${new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(n)}`;
  return `$${n.toLocaleString()}`;
};


export default function ListingDetail() {
  const { id, username, seq } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [listing, setListing] = useState(null);
  const [active, setActive] = useState(0);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [msgText, setMsgText] = useState("");
  const [reportReason, setReportReason] = useState("");
  const [reportCat, setReportCat] = useState("");
  const [reportCats, setReportCats] = useState([]);
  const [msgOpen, setMsgOpen] = useState(false);
  const [promo, setPromo] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const [showPromo, setShowPromo] = useState(false);
  const [fees, setFees] = useState(null);
  const [selVid, setSelVid] = useState(null); // selected variant vid (variant listings)
  const [offerOpen, setOfferOpen] = useState(false); // Make Offer modal
  const [checkoutOpen, setCheckoutOpen] = useState(false); // Checkout confirm modal (method + processing)
  const [shareOpen, setShareOpen] = useState(false); // Share panel (folds the preview card + share actions)
  const shareRef = useRef(null);

  // Resolve either the human-readable /listing/<username>/<n> route or the legacy /listing/<id>.
  const fetchPath = username && seq
    ? `/listings/by-handle/${encodeURIComponent(username)}/${seq}`
    : `/listings/${id}`;
  const load = () => {
    api.get(fetchPath).then(({ data }) => {
      setListing(data);
      api.get(`/listings/${data.listing_id}/comments`).then(({ data: c }) => setComments(c.comments || [])).catch(() => {});
    }).catch(() => navigate("/feed"));
  };
  useEffect(load, [id, username, seq]); // eslint-disable-line
  useEffect(() => setActive(0), [id, username, seq]);
  // Canonicalize legacy /listing/<id> and former-handle URLs → /listing/<current_username>/<n>.
  useEffect(() => {
    if (!listing) return;
    const canon = listingPath(listing);
    if (canon.startsWith("/listing/") && window.location.pathname !== canon) {
      navigate(canon, { replace: true });
    }
  }, [listing]); // eslint-disable-line
  useEffect(() => {
    if (listing?.has_variants) {
      const avail = (listing.variants || []).filter((v) => (v.quantity - (v.sold || 0)) > 0);
      setSelVid((prev) => prev || (avail[0]?.vid ?? (listing.variants || [])[0]?.vid ?? null));
    }
  }, [listing]);
  useEffect(() => {
    api.get("/moderation/categories").then(({ data }) => setReportCats((data.categories || []).filter((c) => c.reporter_selectable && (c.target === "listing" || c.target === "both")))).catch(() => {});
    api.get("/config/fees").then(({ data }) => setFees(data)).catch(() => {});
  }, []);
  useEffect(() => {
    const b = sp.get("boost");
    if (!b) return;
    if (b === "success") {
      const sid = sp.get("session_id");
      (async () => {
        try { if (sid) await api.get(`/boosts/status/${sid}`); } catch {}
        toast.success("Listing boosted — it'll get premium feed placement.");
        load();
        sp.delete("boost"); sp.delete("session_id"); setSp(sp, { replace: true });
      })();
    } else {
      sp.delete("boost"); setSp(sp, { replace: true });
    }
  }, [sp]); // eslint-disable-line

  if (!listing) {
    return (
      <div className="min-h-screen text-foreground">
        <Navbar />
        <div className="max-w-[1280px] mx-auto px-4 py-20 text-center font-mono uppercase text-muted-foreground">Loading optic...</div>
      </div>
    );
  }

  if (listing.gated) {
    const free = listing.gate?.free_count || 5;
    const windowDays = listing.gate?.window_days || 7;
    return (
      <div className="min-h-screen text-foreground">
        <Navbar />
        <div className="max-w-[560px] mx-auto px-4 py-24">
          <div className="border border-sniper bg-surface p-8 text-center" data-testid="metered-gate">
            <Lock className="w-10 h-10 mx-auto text-olive-bright" strokeWidth={1.5} />
            <h1 className="mt-4 font-heading text-3xl uppercase tracking-tight">You've hit the free browsing limit</h1>
            <p className="mt-3 text-sm font-body text-secondary-foreground">
              You've viewed your {free} free listings for the week. {user
                ? "Grab a $5 Buyer Pass for 7 days of full access, or pick a seller plan."
                : "Sign in with an active plan, or grab a $5 Buyer Pass, to keep viewing every optic on the line."}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button onClick={openBuyerPass} data-testid="metered-gate-buyer-pass"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-volt hover:brightness-110 text-black font-heading font-bold uppercase px-6 py-2.5 transition-all">
                <Lock className="w-4 h-4" /> {user ? "Get Buyer Pass — $5" : "Sign in to unlock"}
              </button>
              {user ? (
                <Link to="/pricing" data-testid="metered-gate-plans"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-sniper hover:border-olive text-foreground font-mono uppercase text-sm px-6 py-2.5 transition-colors">
                  See seller plans
                </Link>
              ) : (
                <Link to="/login" data-testid="metered-gate-login"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-sniper hover:border-olive text-foreground font-mono uppercase text-sm px-6 py-2.5 transition-colors">
                  Log in
                </Link>
              )}
            </div>
            <Link to="/feed" data-testid="metered-gate-feed" className="inline-block mt-5 font-mono text-[11px] uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors">
              ← Back to the feed
            </Link>
          </div>
        </div>
        <BuyerPassModal />
      </div>
    );
  }

  // Stable listing id for API calls + child components (URL may be a handle, not an id).
  const lid = listing.listing_id || id;
  // Share links prefer the human-readable /api/l/<username>/<n> slug; fall back to the id.
  const shareSlug = listing.seller_username && listing.seller_seq != null
    ? `${listing.seller_username}/${listing.seller_seq}` : lid;
  const isOwner = user && user.user_id === listing.seller_id;
  const isAdmin = isAdminRole(user?.role);
  const isSold = listing.status === "sold";
  const photos = listing.photos || [];
  const hasPhotos = photos.length > 0;
  // A Want-to-Buy post is an INTENT, not inventory: the poster doesn't own the item, so it never
  // has photos, a shipping weight, or a Buy Now. The whole detail page reshapes around that.
  const isWtb = listing.listing_type === "buy";
  const wtbTimeframeLabel = WTB_TIMEFRAMES.find((t) => t.value === listing.wtb_timeframe)?.label || null;
  const msgLabel = isWtb ? "I have one" : "Message Seller";
  const prevPhoto = () => setActive((a) => (a - 1 + photos.length) % photos.length);
  const nextPhoto = () => setActive((a) => (a + 1) % photos.length);
  const isDealer = !!listing.seller_is_dealer;
  // Sticky offset must clear the Navbar (64) + the logged-in secondary row (44), plus a 16px gap.
  // ADMIN MODE is only a 3px rail now, so admins use the same offset as members.
  const headerOffset = user ? 124 : 80;
  // Optic-completeness nudge is optic-only — knives (and other non-optic categories) don't have
  // tube/magnification/focal-plane, so never prompt for them there.
  const missingSpecs = [];
  if (listing.category !== "Knives & Blades") {
    if (!listing.tube_diameter) missingSpecs.push("tube diameter");
    if (listing.mag_min == null && listing.mag_max == null) missingSpecs.push("magnification");
    if (!listing.focal_plane) missingSpecs.push("focal plane");
    if (listing.weight_oz == null) missingSpecs.push("weight");
  }

  const sendMessage = async () => {
    if (!user) { navigate("/login"); return; }
    if (!msgText.trim()) return;
    try {
      await api.post("/messages", { listing_id: lid, recipient_id: listing.seller_id, text: msgText });
      toast.success("Message sent to seller.");
      setMsgText("");
      setMsgOpen(false);
      navigate("/messages");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login"); return; }
    if (!commentText.trim()) return;
    try {
      const { data } = await api.post(`/listings/${lid}/comments`, { text: commentText });
      setComments([...comments, data]);
      setCommentText("");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const report = async () => {
    if (!user) { navigate("/login"); return; }
    if (!reportCat) { toast.error("Please choose a report category."); return; }
    try {
      await api.post(`/listings/${lid}/report`, { category: reportCat, reason: reportReason || (reportCats.find((c) => c.id === reportCat)?.label || ""), comment: reportReason });
      toast.success("Listing reported. Our team will review it.");
      setReportReason(""); setReportCat("");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const bump = async () => {
    try {
      const { data } = await api.post(`/listings/${lid}/bump`);
      const cd = data.cooldown_hours ? ` You can bump this listing again in ${Math.round(data.cooldown_hours)} hours.` : "";
      toast.success(data.charged > 0
        ? `$${data.charged.toFixed(2)} applied. Moved to the top of Newest.${cd}`
        : `Moved to the top of Newest.${cd}`);
      load();
    } catch (err) {
      if (err.response?.status === 402) { toast.error(formatError(err.response?.data?.detail)); navigate("/credits"); return; }
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const bumpInfo = () => {
    const exempt = !!(user && (user.is_pro || user.is_dealer));
    const fee = fees ? Number(fees.bump_fee) : null;
    const cd = fees ? Number(fees.bump_cooldown_hours) : 0;
    let leftMs = 0;
    if (listing?.last_bumped_at && cd) leftMs = new Date(listing.last_bumped_at).getTime() + cd * 3600000 - Date.now();
    const inCooldown = leftMs > 0;
    const mins = Math.max(0, Math.round(leftMs / 60000));
    const label = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
    return { exempt, fee, inCooldown, label };
  };

  const markSold = async () => {
    try {
      await api.post(`/listings/${lid}/status?status=sold`);
      toast.success("Marked as sold.");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const applyPromo = async () => {
    if (!promo.trim()) return;
    if (!user) { navigate("/login"); return; }
    setPromoBusy(true);
    try {
      const { data } = await api.post("/promo/validate", { code: promo.trim(), listing_id: lid });
      setPromoInfo(data);
      toast.success(`Code applied — ${data.discount_display} off`);
    } catch (err) {
      setPromoInfo(null);
      toast.error(formatError(err.response?.data?.detail));
    } finally { setPromoBusy(false); }
  };

  const buyNow = async () => {
    if (!user) { navigate("/login"); return; }
    // Open the checkout confirm step — the buyer picks card / bank there and sees the
    // method-dependent payment processing added on top before we create the Stripe session.
    setCheckoutOpen(true);
  };

  const seller = listing.seller || {};
  // Variant listing helpers (buy box)
  const variants = listing.variants || [];
  const variantAvail = (v) => (v.quantity - (v.sold || 0)) > 0;
  const selVariant = listing.has_variants ? variants.find((v) => v.vid === selVid) : null;
  const anyVariantAvail = listing.has_variants ? variants.some(variantAvail) : true;
  const displayPrice = listing.has_variants ? (selVariant ? selVariant.price : (listing.variant_price_min ?? listing.price)) : listing.price;
  // ONE-CHIP verification inputs + founding plate gating (revoked suppresses the gold card).
  const revoked = !!listing.seller_verification_revoked;
  const idVerified = !!listing.seller_identity_verified;
  const idYear = listing.seller_identity_year;
  const foundingNumber = listing.seller_founding_number;
  const showFoundingPlate = !!foundingNumber && !revoked;
  const goldCard = showFoundingPlate;

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="max-w-[1280px] mx-auto px-4 pt-6 pb-24 sm:pb-32">
        <Link to="/feed" className="inline-block text-xs font-mono uppercase text-muted-foreground hover:text-foreground" style={{ scrollMarginTop: headerOffset }}>← Back to marketplace</Link>

        <div className="grid lg:grid-cols-[1fr_360px] gap-6 mt-4">
          {/* left */}
          <div className="min-w-0">
            {/* TITLE BLOCK — shown above the photo so buyers see what it is first */}
            <div className="mb-4" data-testid="listing-header">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[10px] uppercase tracking-wider border border-sniper px-2 py-1 text-secondary-foreground">{listing.category}</span>
                {listing.itar && <span className="font-mono text-[10px] uppercase bg-blaze text-black px-2 py-1">ITAR</span>}
                {listing.requires_ffl && <span className="font-mono text-[10px] uppercase border border-blaze text-blaze px-2 py-1">FFL Required</span>}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2.5 break-words" data-testid="listing-title">{listing.title}</h1>
              <div className="flex items-center gap-4 mt-2 text-xs font-mono text-muted-foreground uppercase">
                <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {listing.view_count} views</span>
                <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> {comments.length}</span>
                <span>{timeAgo(listing.created_at)}</span>
              </div>
            </div>

            {/* GALLERY — hidden entirely on a Want-to-Buy (the poster doesn't own the item;
                reference photos are a later phase), and on any photoless listing (no empty void). */}
            {hasPhotos && !isWtb && (<>
            <div className="scope-frame border border-sniper bg-obsidian" data-testid="listing-gallery">
              <div className="relative w-full bg-obsidian overflow-hidden flex items-center justify-center" style={{ aspectRatio: "4 / 3", maxHeight: "560px" }}>
                <ListingImage src={photos[active]} alt={listing.title} className="w-full h-full object-contain" />
                {hasPhotos && photos.length > 1 && (
                  <span className="absolute top-2 right-2 bg-obsidian/85 border border-sniper px-2 py-1 font-mono text-[10px] uppercase tracking-wide text-secondary-foreground" data-testid="gallery-count">
                    {active + 1} / {photos.length}
                  </span>
                )}
                {photos.length > 1 && (
                  <>
                    <button onClick={prevPhoto} data-testid="gallery-prev" aria-label="Previous photo"
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-obsidian/70 hover:bg-obsidian border border-sniper text-foreground transition-colors">
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button onClick={nextPhoto} data-testid="gallery-next" aria-label="Next photo"
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-obsidian/70 hover:bg-obsidian border border-sniper text-foreground transition-colors">
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* thumbnail strip — exactly as many thumbs as the listing has photos, no empty
                dashed slots for buyers (capacity placeholders live only in the create/edit view) */}
            {photos.length > 1 && (
              <div className="flex gap-2 mt-2 overflow-x-auto pb-1" data-testid="thumb-strip">
                {photos.map((p, i) => (
                  <button key={i} onClick={() => setActive(i)} data-testid={`thumb-${i}`}
                    className={`w-16 h-16 shrink-0 border overflow-hidden transition-colors ${active === i ? "border-volt" : "border-sniper hover:border-olive"}`}>
                    <ListingImage src={p} alt="" className="w-full h-full object-cover" small />
                  </button>
                ))}
              </div>
            )}
            </>)}

            {listing.flagged && (
              <div className="mt-4 border border-blaze/50 bg-blaze/10 text-blaze px-4 py-3 flex items-start gap-2" data-testid="fraud-flag">
                <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
                <div className="text-sm font-body">
                  <span className="font-mono uppercase text-xs tracking-wide">Trust alert</span>
                  <p className="mt-0.5">This listing was auto-flagged {listing.flags?.includes("PRICE_ANOMALY_LOW") && "(price far below market) "}for review. Trade with extra caution and use platform messaging.</p>
                </div>
              </div>
            )}

            <div className="mt-6">
              <Crosshair className="mb-5" />

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="listing-specs">
                {isWtb ? (
                  <>
                    <Spec label="Brand" value={listing.brand} to={listing.brand ? `/brand/${brandSlug(listing.brand)}` : undefined} />
                    <Spec label="Model" value={listing.model} />
                    <Spec label="Magnification" value={magLabel(listing)} />
                    <Spec label="Reticle" value={listing.reticle_type} />
                    <Spec label="Will accept" value={(listing.wtb_conditions && listing.wtb_conditions.length) ? listing.wtb_conditions.join(", ") : (listing.condition ? `${listing.condition} or better` : null)} />
                    <Spec label="Budget / Max" value={listing.price > 0 ? fmtDetailPrice(listing.price) : "Open"} />
                    <Spec label="How soon" value={wtbTimeframeLabel} />
                  </>
                ) : (
                  <>
                    <Spec label="Condition" value={listing.condition} />
                    <Spec label="Brand" value={listing.brand} to={listing.brand ? `/brand/${brandSlug(listing.brand)}` : undefined} />
                    <Spec label="Model" value={listing.model} />
                    <Spec label="Magnification" value={magLabel(listing)} />
                    <Spec label="Tube Diameter" value={listing.tube_diameter} />
                    <Spec label="Focal Plane" value={listing.focal_plane && listing.focal_plane !== "N/A" ? listing.focal_plane : null} />
                    <Spec label="Reticle" value={listing.reticle_type} />
                    <Spec label="Weight" value={listing.weight_oz != null ? `${listing.weight_oz} oz` : null} />
                    <Spec label="Mount" value={listing.mount_type} />
                    <Spec label="Range" value={listing.mag_range} />
                    <Spec label="Color" value={listing.color} />
                    <Spec label="Laser Type" value={listing.category === "Lasers" ? listing.laser_type : null} />
                    <Spec label="Wavelength" value={listing.category === "Lasers" ? listing.laser_wavelength : null} />
                    {listing.category === "Knives & Blades" && (() => {
                      const ks = listing.knife_specs || {};
                      return (
                        <>
                          <Spec label="Blade length" value={ks.blade_length_in ? `${ks.blade_length_in} in` : null} />
                          <Spec label="Overall length" value={ks.overall_length_in ? `${ks.overall_length_in} in` : null} />
                          <Spec label="Blade steel" value={ks.blade_steel} />
                          <Spec label="Blade grind" value={ks.blade_grind} />
                          <Spec label="Edge type" value={ks.edge_type} />
                          <Spec label="Point / tip" value={ks.point_style} />
                          <Spec label="Handle" value={ks.handle_material} />
                          <Spec label="Tang" value={ks.tang} />
                          <Spec label="Lock" value={ks.locking_mechanism && ks.locking_mechanism !== "Fixed blade / N/A" ? ks.locking_mechanism : null} />
                          <Spec label="Sheath / pouch" value={ks.includes_sheath ? "Included" : null} />
                        </>
                      );
                    })()}
                    <Spec label="Age" value={ageLabel(listing)} />
                    <Spec label="Warranty" value={listing.warranty_remaining} />
                    <Spec label="Shipping" value={{ ship: "Ship", pickup: "Local Pickup", both: "Ship / Pickup" }[listing.shipping]} />
                  </>
                )}
              </div>

              {isOwner && !isWtb && !isSold && (missingSpecs.length > 0 || listing.mag_parse_failed) && (
                <div className="mt-3 border border-olive-bright/40 bg-olive/10 px-4 py-3 flex items-start gap-2" data-testid="owner-spec-prompt">
                  <Info className="w-4 h-4 text-olive-bright shrink-0 mt-0.5" />
                  <div className="text-sm font-body text-secondary-foreground">
                    <span className="font-mono uppercase text-xs text-olive-bright tracking-wide">Complete your specs</span>
                    <p className="mt-0.5">
                      {missingSpecs.length > 0
                        ? `Add ${missingSpecs.join(", ")} so buyers' saved searches surface this listing. `
                        : "We couldn't read the magnification automatically — set it so filters work. "}
                      <button onClick={() => navigate(`/edit/${lid}`)} className="text-olive-bright underline" data-testid="owner-spec-edit">Edit listing →</button>
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-8 border border-sniper bg-surface p-4 sm:p-5" data-testid="seller-comments-box">
                <h2 className="font-heading text-lg uppercase tracking-tight mb-2 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-olive-bright" /> Seller Comments
                </h2>
                <p className="text-secondary-foreground font-body whitespace-pre-wrap leading-relaxed" data-testid="listing-description">{listing.description || "No description provided."}</p>
              </div>

              {listing.open_to_trades && (
                <div className="mt-4 border border-sniper bg-surface p-4">
                  <div className="flex items-center gap-2 text-volt font-mono text-xs uppercase tracking-wide"><Repeat className="w-4 h-4" /> Open to trade</div>
                  {listing.trade_preferences && <p className="mt-1 text-sm text-secondary-foreground font-body">{listing.trade_preferences}</p>}
                </div>
              )}
            </div>

            {/* comments */}
            <div className="mt-10">
              <h2 className="font-heading text-2xl uppercase tracking-tight mb-3">Comments ({comments.length})</h2>
              <Crosshair className="my-3" />
              <form onSubmit={addComment} className="flex gap-2">
                <input value={commentText} onChange={(e) => setCommentText(e.target.value)} data-testid="comment-input"
                  disabled={!!user && !user.identity_verified}
                  placeholder={!user ? "Log in to comment" : (user.identity_verified ? "Ask a public question..." : "Verify your ID to comment")}
                  className="flex-1 bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed" />
                <button type="submit" data-testid="comment-submit" disabled={!!user && !user.identity_verified} className="bg-olive hover:bg-olive-bright disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 transition-colors"><Send className="w-4 h-4" /></button>
              </form>
              <div className="mt-4 space-y-3">
                {comments.map((c) => (
                  <div key={c.comment_id} className="border-l-2 border-sniper pl-3 py-1" data-testid={`comment-${c.comment_id}`}>
                    <div className="flex items-center gap-2 text-xs">
                      <Link to={`/u/${c.username}`} className="font-mono text-olive-bright">@{c.username}</Link>
                      <span className="text-muted-foreground font-mono">{timeAgo(c.created_at)}</span>
                    </div>
                    <p className="text-sm text-secondary-foreground font-body mt-0.5">{c.text}</p>
                  </div>
                ))}
                {comments.length === 0 && <p className="text-sm text-muted-foreground font-body">No comments yet.</p>}
              </div>
            </div>
          </div>

          {/* right sidebar — BUY BOX. Strict order: price → location/ship → BUY NOW (nothing
              between) → payment-protected → collapsed "Have a promo code?" → hairline →
              MESSAGE SELLER (ghost) → seller panel (ONE verification chip) → founding plate →
              report/block footer. A revoked seller gets NO gold card and NO founding plate. */}
          <div>
            <div className="border bg-surface p-5 sticky" style={{ top: headerOffset, borderColor: goldCard ? "#7A6428" : "#2E2F31" }} data-testid="buy-box">
              {/* 1 — PRICE */}
              <div className="flex items-baseline gap-2 min-w-0">
                {listing.status === "sold" && <span className="font-heading text-xl text-blaze uppercase shrink-0">SOLD</span>}
                {listing.status === "pending" && <span className="font-heading text-xl text-volt uppercase shrink-0" data-testid="pending-badge">SALE PENDING</span>}
                <span className={`font-heading text-[44px] leading-none font-black min-w-0 truncate tabular-nums ${listing.listing_type === "buy" ? "text-foreground" : isDealer ? "text-steel" : ""}`}
                  style={listing.listing_type !== "buy" && !isDealer ? { color: "#cfe86b" } : undefined} data-testid="listing-price">
                  {listing.listing_type === "buy" ? "ISO" : fmtDetailPrice(displayPrice)}
                </span>
              </div>
              {listing.has_variants && (
                <div className="mt-1 font-mono text-[11px] uppercase" style={{ color: "#8E9195" }} data-testid="variant-price-caption">
                  {selVariant ? selVariant.label : `From ${fmtDetailPrice(listing.variant_price_min ?? listing.price)}`}
                  {` · ${variants.length} option${variants.length === 1 ? "" : "s"}`}
                </div>
              )}
              {isWtb && (
                <div className="mt-1 font-mono text-[11px] uppercase" style={{ color: "#8E9195" }} data-testid="wtb-budget-line">
                  {listing.price > 0 ? `Budget: up to ${fmtDetailPrice(listing.price)}` : "Open budget"}
                </div>
              )}
              {/* 2 — LOCATION */}
              <div className="flex items-center gap-2 mt-1 text-[11px] font-mono uppercase" style={{ color: "#8E9195" }}>
                <MapPin className="w-3.5 h-3.5" /> {listing.city || "—"}{listing.state ? `, ${listing.state}` : ""}
                {!isWtb && (<>
                  <span className="text-muted-foreground">·</span>
                  <Truck className="w-3.5 h-3.5" /> {{ ship: "Ship", pickup: "Pickup", both: "Ship/Pickup" }[listing.shipping]}
                </>)}
              </div>
              {listing.ship_mode === "own_label" && listing.shipping !== "pickup" && (
                <div className="mt-1 font-mono text-[11px] uppercase" style={{ color: "#8E9195" }} data-testid="buy-shipping-line">
                  {listing.free_shipping ? "Free shipping" : `+ $${((listing.flat_shipping_cents || 0) / 100).toFixed(2)} shipping`}
                </div>
              )}

              {/* VARIANT PICKER — one listing, multiple options. Buyer selects before Buy Now. */}
              {listing.has_variants && !isWtb && listing.status !== "sold" && (
                <div className="mt-4 space-y-1.5" data-testid="variant-picker">
                  <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Choose an option</div>
                  {variants.map((v) => {
                    const avail = variantAvail(v);
                    const selected = v.vid === selVid;
                    const rem = v.quantity - (v.sold || 0);
                    // Seller-only "running low" nudge: only when an option started with 2+ and has
                    // sold down to its last 1-2 units (a true relist prompt, not every single-qty item).
                    const lowStock = isOwner && v.quantity >= 2 && (v.sold || 0) >= 1 && rem >= 1 && rem <= 2;
                    return (
                      <button
                        key={v.vid}
                        type="button"
                        disabled={!avail}
                        onClick={() => avail && setSelVid(v.vid)}
                        data-testid={`variant-option-${v.vid}`}
                        data-selected={selected || undefined}
                        className={`w-full flex items-center justify-between gap-3 border px-3 py-2 text-left transition-colors ${
                          !avail ? "border-sniper opacity-45 cursor-not-allowed"
                          : selected ? "border-olive-bright bg-olive/10"
                          : "border-sniper hover:border-olive"
                        }`}
                      >
                        <span className="min-w-0">
                          <span className="block font-body text-sm text-foreground truncate">{v.label}</span>
                          <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                            {v.condition || listing.condition}{!avail && " · Sold out"}
                            {lowStock && (
                              <span data-testid={`variant-lowstock-${v.vid}`} className="inline-block border border-blaze/60 text-blaze px-1 py-0.5 leading-none">
                                {`${rem} left`}
                              </span>
                            )}
                          </span>
                        </span>
                        <span className="font-heading font-bold tabular-nums shrink-0" style={{ color: "#cfe86b" }}>
                          {fmtDetailPrice(v.price)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}


              {/* 3 — BUY NOW (nothing renders between price and this). Owner sees management
                   controls in this slot; a pending sale shows the pending note. */}
              {isOwner ? (
                <div className="space-y-2 mt-4" data-testid="owner-controls">
                  {/* A SOLD listing is locked — the seller loses the edit path entirely (mirrors the
                      server 409). Admins keep an override button below to correct genuine errors. */}
                  {!isSold && (
                    <button onClick={() => navigate(`/edit/${lid}`)} data-testid="owner-edit-btn" className="w-full flex items-center justify-center gap-2 border border-sniper hover:border-olive text-foreground font-mono uppercase text-sm py-2.5 transition-colors"><Pencil className="w-4 h-4" /> Edit</button>
                  )}
                  {isSold && (
                    <div className="w-full flex items-center justify-center gap-2 border border-sniper/60 text-muted-foreground font-mono uppercase text-xs py-2.5" data-testid="owner-sold-locked">
                      <Lock className="w-3.5 h-3.5" /> Sold — listing locked
                    </div>
                  )}
                  {listing.status === "active" && !isWtb && (() => {
                    const bi = bumpInfo();
                    const label = bi.inCooldown
                      ? `Bump available in ${bi.label}`
                      : bi.exempt ? "Bump To Top (Free)"
                      : bi.fee != null ? `Bump — $${bi.fee.toFixed(2)}` : "Bump To Top";
                    return (
                      <button onClick={bump} disabled={bi.inCooldown} data-testid="bump-btn"
                        title={bi.inCooldown ? "This listing was bumped recently" : "Move to the top of Newest"}
                        className="w-full flex items-center justify-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 disabled:cursor-not-allowed text-white font-heading font-bold uppercase py-2.5 transition-colors">
                        <ArrowUpToLine className="w-4 h-4" /> {label}
                      </button>
                    );
                  })()}
                  {listing.status === "active" && !isWtb && <BoostListingButton listingId={lid} onChanged={load} />}
                  {listing.status === "active" && <CloseListingButton listingId={lid} onDone={load} listingTitle={listing.title} />}
                </div>
              ) : listing.listing_type === "sell" && listing.price > 0 && listing.status === "active" ? (
                <div className="mt-4" data-testid="buy-now-block">
                  {user && !user.identity_verified ? (
                    <>
                      <button onClick={() => navigate("/verify")} data-testid="buy-now-verify"
                        className="w-full flex items-center justify-center gap-2 border border-olive text-olive-bright hover:bg-olive/10 font-heading font-bold uppercase text-lg py-3 transition-colors">
                        <ShieldCheck className="w-5 h-5" /> Verify your ID to buy
                      </button>
                      <p className="mt-1.5 flex items-center justify-center gap-1.5 text-[11px] font-mono uppercase text-muted-foreground">
                        <Lock className="w-3 h-3 text-olive-bright" /> Buyers are ID-verified before any money moves
                      </p>
                    </>
                  ) : (
                    <>
                      <button onClick={buyNow} data-testid="buy-now-btn"
                        disabled={listing.has_variants && (!selVid || !anyVariantAvail)}
                        style={{ backgroundColor: "#cfe86b", color: "#0a0c05" }}
                        className="w-full flex items-center justify-center gap-2 hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed font-heading font-bold uppercase text-lg py-3 transition-all">
                        <ShoppingCart className="w-5 h-5" /> Buy Now
                      </button>
                      {listing.accept_offers && (!listing.has_variants || (selVid && anyVariantAvail)) && (
                        <button onClick={() => setOfferOpen(true)} data-testid="make-offer-btn"
                          className="w-full mt-2 flex items-center justify-center gap-2 border border-olive hover:bg-olive/10 text-olive-bright font-heading font-bold uppercase text-sm py-2.5 transition-colors">
                          <Tag className="w-4 h-4" /> Make Offer
                        </button>
                      )}
                      <p className="mt-1.5 flex items-center justify-center gap-1.5 text-[10px] font-mono uppercase" style={{ color: "#76797D" }}>
                        <Lock className="w-3 h-3" /> Payment protected — released after delivery is confirmed
                      </p>
                      {/* Collapsed promo — the field still exists and still applies codes; it just
                           opens on click so nothing competes with the Buy Now action. */}
                      {promoInfo ? (
                        <div className="mt-2 flex items-center justify-between font-mono text-xs border border-olive-bright/40 bg-olive/5 px-3 py-2" data-testid="promo-applied">
                          <span className="text-olive-bright uppercase">{promoInfo.code} · −{promoInfo.discount_display}</span>
                          <span className="text-foreground">You pay {promoInfo.buyer_total_display}</span>
                        </div>
                      ) : showPromo ? (
                        <div className="mt-2 flex gap-2" data-testid="promo-row">
                          <input value={promo} onChange={(e) => { setPromo(e.target.value.toUpperCase()); setPromoInfo(null); }}
                            placeholder="Promo code" data-testid="promo-input" autoFocus
                            className="flex-1 bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-mono text-sm uppercase" />
                          <button onClick={applyPromo} disabled={promoBusy || !promo.trim()} data-testid="promo-apply-btn"
                            className="border border-sniper hover:border-olive text-foreground font-mono uppercase text-xs px-4 transition-colors disabled:opacity-50">
                            {promoBusy ? "…" : "Apply"}
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => setShowPromo(true)} data-testid="promo-toggle"
                          style={{ color: "#76797D" }}
                          className="mt-2 mx-auto block font-mono text-[11px] uppercase tracking-wide underline underline-offset-2 hover:text-olive-bright transition-colors">
                          Have a promo code?
                        </button>
                      )}
                    </>
                  )}
                </div>
              ) : listing.listing_type === "sell" && listing.status === "pending" ? (
                <div className="mt-4 border border-volt/40 bg-volt/5 px-3 py-2.5" data-testid="listing-pending-note">
                  <span className="font-mono uppercase text-volt text-[11px]">Sale pending</span>
                  <p className="mt-1 text-[12px] font-body text-secondary-foreground">A buyer is completing this purchase. It'll return to the marketplace if the sale doesn't go through.</p>
                </div>
              ) : null}

              {/* ADMIN OVERRIDE — admins/super-admins can correct a genuine error on a locked SOLD
                  record (server permits + audit-logs it). Shown to any admin viewing a sold listing,
                  including one they don't own. Distinct from the seller's (removed) edit control. */}
              {isAdmin && isSold && (
                <button onClick={() => navigate(`/edit/${lid}`)} data-testid="admin-edit-sold-btn"
                  className="mt-3 w-full flex items-center justify-center gap-2 border border-[#C2600F]/60 bg-[#C2600F]/10 text-[#E8C46A] hover:bg-[#C2600F]/20 font-mono uppercase text-xs py-2.5 transition-colors">
                  <ShieldAlert className="w-4 h-4" /> Edit sold listing (admin override)
                </button>
              )}

              {/* SHARE — merged preview card + share actions, shown to any viewer (owner OR buyer)
                  so anyone can spread the listing. Replaces the old floating top-right dropdown. */}
              {!isWtb && (
                <SharePreview
                  listingId={lid}
                  shareUrl={`${window.location.origin}/api/l/${shareSlug}`}
                  title={listing.title}
                  open={shareOpen}
                  setOpen={setShareOpen}
                  panelRef={shareRef}
                />
              )}

              <div className="my-4 border-t" style={{ borderColor: "#2E2F31" }} />

              {/* 5 — MESSAGE SELLER (ghost). Owner never sees this. */}
              {!isOwner && (
                !user ? (
                  <button onClick={() => navigate("/login")} data-testid="message-seller-btn"
                    style={{ borderColor: "#2E2F31", color: "#8E9195" }}
                    className="w-full flex items-center justify-center gap-2 border bg-transparent hover:border-olive hover:text-foreground font-mono uppercase text-sm py-2.5 transition-colors">
                    <MessageSquare className="w-4 h-4" /> {msgLabel}
                  </button>
                ) : !user.identity_verified ? (
                  <button onClick={() => navigate("/verify")} data-testid="message-seller-verify"
                    className="w-full flex items-center justify-center gap-2 border border-olive text-olive-bright hover:bg-olive/10 font-mono uppercase text-sm py-2.5 transition-colors">
                    <ShieldCheck className="w-4 h-4" /> Verify your ID to message
                  </button>
                ) : (
                  <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
                    <DialogTrigger asChild>
                      <button data-testid="message-seller-btn" style={{ borderColor: "#2E2F31", color: "#8E9195" }}
                        className="w-full flex items-center justify-center gap-2 border bg-transparent hover:border-olive hover:text-foreground font-mono uppercase text-sm py-2.5 transition-colors">
                        <MessageSquare className="w-4 h-4" /> {msgLabel}
                      </button>
                    </DialogTrigger>
                    <DialogContent className="bg-surface border-sniper text-foreground rounded-none">
                      <DialogHeader><DialogTitle className="font-heading uppercase">Message @{listing.seller_username}</DialogTitle></DialogHeader>
                      <DialogDescription className="text-xs font-mono uppercase text-muted-foreground">Re: {listing.title}</DialogDescription>
                      <textarea rows={4} value={msgText} onChange={(e) => setMsgText(e.target.value)} data-testid="message-input"
                        placeholder={isWtb ? "I've got one of these — still looking?" : "Is this still available? Any lower?"} className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground transition-colors" />
                      <DialogFooter>
                        <button onClick={sendMessage} data-testid="message-send" className="bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase px-6 py-2.5 transition-colors">Send</button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )
              )}

              {/* 6 — SELLER PANEL (inset box, exactly ONE verification chip) */}
              <div className={`${!isOwner ? "mt-4" : ""} border p-4`} style={{ backgroundColor: "#0D0E0F", borderColor: goldCard ? "#7A6428" : "#2E2F31" }} data-testid="seller-panel">
                <Link to={`/u/${listing.seller_username}`} className="flex items-center gap-3 group" data-testid="seller-link">
                  <div className={`w-11 h-11 shrink-0 border bg-obsidian flex items-center justify-center overflow-hidden ${isDealer ? "border-steel/60 rounded-md" : "border-olive-bright/60 rounded-full"}`}>
                    {seller.avatar ? <img src={seller.avatar} alt="" className="w-full h-full object-cover" /> : <span className="font-heading text-xl">{(listing.seller_username || "?")[0]?.toUpperCase()}</span>}
                  </div>
                  <div className="min-w-0">
                    <span className={`flex items-center truncate font-heading text-lg transition-colors ${isDealer ? "text-steel group-hover:text-steel-bright" : "text-olive-bright group-hover:text-foreground"}`}>
                      <span className="truncate">@{listing.seller_username}</span>
                      {listing.seller_vouch_username && (
                        <VouchMark inviter={listing.seller_vouch_username} size={16} testid="listing-vouch-mark" />
                      )}
                    </span>
                    <div className="flex items-center gap-2 text-[11px] font-mono uppercase mt-0.5">
                      {seller.feedback_count >= 1 ? (
                        <>
                          <span className="flex items-center gap-0.5 text-muted-foreground"><Star className="w-3 h-3 text-volt" /> {seller.feedback_score}</span>
                          <span className="text-muted-foreground">· {seller.feedback_count} {seller.feedback_count === 1 ? "review" : "reviews"}</span>
                        </>
                      ) : (
                        <span style={{ color: "#8E9195" }}>No reviews yet</span>
                      )}
                    </div>
                  </div>
                </Link>
                <div className="mt-3">
                  <SellerVerificationChip revoked={revoked} dealer={isDealer} idVerified={idVerified} idYear={idYear} testid="seller-verification-chip" />
                </div>
                {seller.created_at && (
                  <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] font-mono uppercase text-muted-foreground">
                    <div>Joined<br /><span className="text-secondary-foreground">{new Date(seller.created_at).toLocaleDateString()}</span></div>
                    {seller.feedback_count >= 1 && Number.isFinite(seller.response_rate) && (
                      <div>Response<br /><span className="text-secondary-foreground">{seller.response_rate}%</span></div>
                    )}
                  </div>
                )}
              </div>

              {/* 7 — FOUNDING PLATE (suppressed entirely when revoked) */}
              {showFoundingPlate && <FoundingPlate number={foundingNumber} testid="founding-plate" />}

              {/* 8 — REPORT / BLOCK footer */}
              {!isOwner && (
                <div className="mt-4 pt-3 border-t border-sniper flex items-center justify-between gap-2" data-testid="buy-box-footer">
                  <Dialog>
                    <DialogTrigger asChild>
                      <button data-testid="report-btn" className="flex items-center gap-1.5 text-muted-foreground hover:text-blaze font-mono uppercase text-[11px] py-1 transition-colors">
                        <Flag className="w-3.5 h-3.5" /> Report
                      </button>
                    </DialogTrigger>
                    <DialogContent className="bg-surface border-sniper text-foreground rounded-none">
                      <DialogHeader><DialogTitle className="font-heading uppercase flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-blaze" /> Report Listing</DialogTitle></DialogHeader>
                      <DialogDescription className="sr-only">Report this listing for moderation review</DialogDescription>
                      <label className="font-mono text-[10px] uppercase text-muted-foreground">What's wrong with it?</label>
                      <select value={reportCat} onChange={(e) => setReportCat(e.target.value)} data-testid="report-category"
                        className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground font-mono text-sm transition-colors">
                        <option value="" disabled>Choose a category…</option>
                        {reportCats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                      </select>
                      {reportCat && <p className="text-[11px] font-mono text-muted-foreground leading-snug">{reportCats.find((c) => c.id === reportCat)?.definition}</p>}
                      <textarea rows={3} value={reportReason} onChange={(e) => setReportReason(e.target.value)} data-testid="report-input"
                        placeholder="Add any details for the moderation team (optional)…" className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground transition-colors" />
                      <DialogFooter>
                        <button onClick={report} data-testid="report-send" className="bg-blaze hover:brightness-110 text-black font-heading font-bold uppercase px-6 py-2.5 transition-colors">Submit Report</button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                  {user && listing.seller_username && (
                    <div data-testid="listing-block-seller">
                      <BlockUserButton username={listing.seller_username} blocked={false} onChanged={() => navigate("/feed")} />
                    </div>
                  )}
                </div>
              )}

              {/* Promoted-placement FTC disclosure — kept OUT of the price→Buy Now path, in the footer. */}
              {listing.boosted && (
                <div className="mt-3 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wide text-muted-foreground" data-testid="promoted-disclosure">
                  <Megaphone className="w-3 h-3 text-foreground/50" /> Promoted placement — the seller boosted this listing
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {offerOpen && (
        <MakeOfferModal open={offerOpen} onClose={() => setOfferOpen(false)} listing={listing} variant={selVariant} />
      )}
      {checkoutOpen && (
        <CheckoutModal open={checkoutOpen} onClose={() => setCheckoutOpen(false)}
          listingId={lid} title={listing.title}
          promoCode={promoInfo ? promoInfo.code : undefined}
          variantId={listing.has_variants ? selVid : undefined} />
      )}
    </div>
  );
}
